import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  ChevronRight,
  TrendingUp,
  Award,
  AlertTriangle,
  Users,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Progress } from "@/shared/ui/progress";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Pagination } from "@/shared/ui/pagination";
import { useClampedPage } from "@/shared/hooks/useClampedPage";
import type { EvaluationByTaskItem } from "../../api/coachingService";
import { fmtDatetime, scoreTone, taskStatusLabel, taskStatusType } from "./helpers";

const PAGE_SIZE = 15;

type SortKey = "default" | "avgDesc" | "passAsc" | "riskDesc";

type Props = {
  tasks: EvaluationByTaskItem[];
  search: string;
  onSearchChange: (v: string) => void;
  sortKey: SortKey;
  onSortChange: (k: SortKey) => void;
  onShowDetail: (taskId: number) => void;
};

export function EvaluationsTab({
  tasks,
  search,
  onSearchChange,
  sortKey,
  onSortChange,
  onShowDetail,
}: Props) {
  const { t } = useTranslation("coaching");
  // 页面级聚合(全部任务汇总,加权平均)
  const totals = tasks.reduce(
    (acc, t) => {
      acc.evaluated += t.evaluatedCount;
      acc.assigned += t.assignedCount;
      acc.pass += t.passCount;
      acc.risk += t.riskCount;
      acc.scoreSum += (t.avgScore ?? 0) * t.evaluatedCount;
      return acc;
    },
    { evaluated: 0, assigned: 0, pass: 0, risk: 0, scoreSum: 0 }
  );
  const overallAvg = totals.evaluated > 0 ? totals.scoreSum / totals.evaluated : null;
  const overallPassRate = totals.evaluated > 0 ? totals.pass / totals.evaluated : null;
  const overallCompletionRate = totals.assigned > 0 ? totals.evaluated / totals.assigned : null;

  // 搜索 + 排序
  const filtered = search
    ? tasks.filter((t) => {
        const q = search.toLowerCase();
        return (
          t.taskName.toLowerCase().includes(q) ||
          (t.scenarioName ?? "").toLowerCase().includes(q) ||
          t.status.toLowerCase().includes(q)
        );
      })
    : tasks;

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === "avgDesc") return (b.avgScore ?? -1) - (a.avgScore ?? -1);
    if (sortKey === "passAsc") {
      const ar = a.evaluatedCount > 0 ? a.passCount / a.evaluatedCount : 2; // 无数据排末尾
      const br = b.evaluatedCount > 0 ? b.passCount / b.evaluatedCount : 2;
      return ar - br;
    }
    if (sortKey === "riskDesc") return b.riskCount - a.riskCount;
    return 0;
  });

  // F 分页补齐:客户端分页(0 基;Hero KPI/排序/搜索仍跨全量,只截当前页渲染)。
  const [page, setPage] = useState(0);
  // 改搜索/排序即回第 1 页(codex P2:否则停在后页看不到新结果的前几行)。
  useEffect(() => {
    setPage(0);
  }, [search, sortKey]);
  const maxPage = Math.max(0, Math.ceil(sorted.length / PAGE_SIZE) - 1);
  useClampedPage({ page, maxPage, setPage }); // 删行/结果变少越界时 clamp 回末页
  const pageRows = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="space-y-5 mt-4">
      {/* 顶部 Hero KPI — 全部任务视角 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="shadow-sm border-l-4 border-l-primary fx-hover-lift">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{t("evaluationsTab.kpiEvaluated")}</p>
              <p className="text-2xl font-bold mt-1 leading-none tabular-nums">
                {totals.evaluated}
              </p>
              <p className="text-2xs text-muted-foreground mt-1.5 tabular-nums">
                {t("evaluationsTab.kpiCompletionRate", {
                  rate:
                    overallCompletionRate !== null
                      ? `${Math.round(overallCompletionRate * 100)}%`
                      : "—",
                })}
                {t("evaluationsTab.kpiAssigned", { count: totals.assigned })}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-4 w-4 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-success fx-hover-lift">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{t("evaluationsTab.kpiAvg")}</p>
              <p className="text-2xl font-bold mt-1 leading-none tabular-nums">
                {overallAvg !== null ? overallAvg.toFixed(1) : "—"}
              </p>
              <p className="text-2xs text-muted-foreground mt-1.5">
                {t("evaluationsTab.kpiAvgHint")}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-success/10">
              <TrendingUp className="h-4 w-4 text-success" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-warning fx-hover-lift">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{t("evaluationsTab.kpiPass")}</p>
              <p className="text-2xl font-bold mt-1 leading-none tabular-nums">
                {overallPassRate !== null ? `${Math.round(overallPassRate * 100)}%` : "—"}
              </p>
              <p className="text-2xs text-muted-foreground mt-1.5 tabular-nums">
                {t("evaluationsTab.kpiPassHint", {
                  pass: totals.pass,
                  evaluated: totals.evaluated,
                })}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-warning/10">
              <Award className="h-4 w-4 text-warning" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-destructive fx-hover-lift fx-glow">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">{t("evaluationsTab.kpiRisk")}</p>
              <p className="text-2xl font-bold mt-1 leading-none tabular-nums text-destructive">
                {totals.risk}
              </p>
              <p className="text-2xs text-muted-foreground mt-1.5">
                {t("evaluationsTab.kpiRiskHint")}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-destructive/10">
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 工具栏 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("evaluationsTab.searchPlaceholder")}
              className="pl-9 w-64"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <Select value={sortKey} onValueChange={(v) => onSortChange(v as SortKey)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder={t("evaluationsTab.sortPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">{t("evaluationsTab.sortDefault")}</SelectItem>
              <SelectItem value="avgDesc">{t("evaluationsTab.sortAvgDesc")}</SelectItem>
              <SelectItem value="passAsc">{t("evaluationsTab.sortPassAsc")}</SelectItem>
              <SelectItem value="riskDesc">{t("evaluationsTab.sortRiskDesc")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* audit-0713 C2:导出未实现,移除永久 disabled 死按钮(tooltip 因 pointer-events-none 也弹不出);功能落地随实现回来 */}
      </div>

      {/* 任务聚合表格 */}
      <Card className="shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px]">{t("evaluationsTab.headerTask")}</TableHead>
              <TableHead>{t("evaluationsTab.headerScenario")}</TableHead>
              <TableHead className="w-44">{t("evaluationsTab.headerProgress")}</TableHead>
              <TableHead className="w-24">{t("evaluationsTab.headerAvg")}</TableHead>
              <TableHead className="w-44">{t("evaluationsTab.headerPassRate")}</TableHead>
              <TableHead className="w-40">{t("evaluationsTab.headerExtreme")}</TableHead>
              <TableHead className="w-24">{t("evaluationsTab.headerStatus")}</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-12">
                  {search ? t("evaluationsTab.emptyMatch") : t("evaluationsTab.emptyTasks")}
                </TableCell>
              </TableRow>
            )}
            {pageRows.map((task, i) => {
              const evalRate =
                task.assignedCount > 0
                  ? Math.round((task.evaluatedCount / task.assignedCount) * 100)
                  : 0;
              const passRate =
                task.evaluatedCount > 0
                  ? Math.round((task.passCount / task.evaluatedCount) * 100)
                  : null;
              // 高风险标记:合格率 <60% 或 风险员工 ≥3
              const isHighRisk = (passRate !== null && passRate < 60) || task.riskCount >= 3;

              return (
                <TableRow
                  key={task.taskId}
                  className={`hover:bg-muted/40 transition-colors cursor-pointer fx-enter fx-press ${
                    isHighRisk ? "border-l-4 border-l-destructive" : ""
                  }`}
                  style={{ animationDelay: `${Math.min(i, 7) * 30}ms` }}
                  onClick={() => onShowDetail(task.taskId)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1.5">
                      <span>{task.taskName}</span>
                      {isHighRisk && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive px-1.5 py-0.5 text-2xs font-semibold">
                          <AlertTriangle className="h-3 w-3" />
                          {t("evaluationsTab.riskBadge", { count: task.riskCount })}
                        </span>
                      )}
                    </div>
                    {task.deadline && (
                      <div className="text-2xs text-muted-foreground mt-0.5">
                        {t("evaluationsTab.deadlinePrefix", { date: fmtDatetime(task.deadline) })}
                      </div>
                    )}
                  </TableCell>

                  <TableCell>
                    {task.scenarioName ? (
                      <Badge variant="outline" className="font-normal normal-case tracking-normal">
                        {task.scenarioName}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={evalRate} className="h-1.5 flex-1" />
                      <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                        {task.evaluatedCount}/{task.assignedCount}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    {task.avgScore !== null ? (
                      <span
                        className={`text-base font-bold tabular-nums ${scoreTone(task.avgScore, task.passScore)}`}
                      >
                        {task.avgScore.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {passRate !== null ? (
                      <div className="flex items-center gap-2">
                        <Progress
                          value={passRate}
                          className={`h-1.5 flex-1 ${passBarTone(passRate)}`}
                        />
                        <span className="text-xs tabular-nums whitespace-nowrap">{passRate}%</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <div className="space-y-0.5">
                      {task.topScore !== null && (
                        <div className="flex items-center gap-1 text-xs">
                          <ArrowUp className="h-3 w-3 text-success" />
                          <span className="font-bold tabular-nums">{task.topScore}</span>
                          <span className="text-muted-foreground truncate max-w-[5rem]">
                            {task.topEmployeeName ?? "—"}
                          </span>
                        </div>
                      )}
                      {task.bottomScore !== null && (
                        <div className="flex items-center gap-1 text-xs">
                          <ArrowDown className="h-3 w-3 text-destructive" />
                          <span className="font-bold tabular-nums">{task.bottomScore}</span>
                          <span className="text-muted-foreground truncate max-w-[5rem]">
                            {task.bottomEmployeeName ?? "—"}
                          </span>
                        </div>
                      )}
                      {task.topScore === null && task.bottomScore === null && (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell>
                    <StatusBadge type={taskStatusType(task.status)}>
                      {taskStatusLabel(task.status)}
                    </StatusBadge>
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowDetail(task.taskId);
                      }}
                      title={t("evalTaskDetail.actionView")}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {sorted.length > PAGE_SIZE && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={sorted.length}
          showTotal
          onPageChange={setPage}
        />
      )}
    </div>
  );
}

function passBarTone(rate: number) {
  if (rate >= 80) return "[&>div]:bg-success";
  if (rate >= 60) return "[&>div]:bg-warning";
  return "[&>div]:bg-destructive";
}
